import { HttpErrorResponse } from "@angular/common/http";
import {
  Ng2StateDeclaration,
  StateDeclaration,
  Transition,
} from "@uirouter/angular";
import { merge } from "lodash";
import { AccountOverviewComponent } from "../account-overview/account-overview.component";
import { AirbrakeService } from "../airbrake/airbrake.service";
import { DeployViewComponent } from "../deploy-view/deploy-view.component";
import { ErrorsComponent } from "../errors/errors.component";
import { FilterParamsService } from "../group-filter/filter-params/filter-params.service";
import { GroupFilterComponent } from "../group-filter/group-filter.component";
import { GroupListComponent } from "../group-list/group-list.component";
import { DeployService } from "../models/deploy.service";
import { GroupService } from "../models/group.service";
import { NoticeService } from "../models/notice.service";
import { ProjectService } from "../models/project.service";
import { UserService } from "../models/user.service";
import { NoticeOverviewComponent } from "../notice-view/notice-overview/notice-overview.component";
import { NoticeViewComponent } from "../notice-view/notice-view.component";
import { OnboardingChecklistComponent } from "../onboarding/onboarding-checklist.component";
import { OnboardingProgressComponent } from "../onboarding/onboarding-progress.component";
import { ProjectAlertsComponent } from "../project-alerts/project-alerts.component";
import { ProjectDeploysComponent } from "../project-deploys/project-deploys.component";
import { ProjectDetailComponent } from "../project-detail/project-detail.component";
import { ProjectLanguageIconComponent } from "../project-language-icon/project-language-icon.component";
import { ProjectNavComponent } from "../project-nav/project-nav.component";
import { ProjectPerformanceComponent } from "../project-performance/project-performance.component";
import { RouteViewComponent } from "../project-performance/route-view/route-view.component";
import { ProjectPickerComponent } from "../project-picker/project-picker.component";
import { ProjectTrendsComponent } from "../project-trends/project-trends.component";
import { RootUrlHandlerService } from "../root-url-handler/root-url-handler.service";
import { AccountViewComponent } from "../workbench/account-view.component";
import { AlertsViewComponent } from "../workbench/alerts-view.component";
import { DeploysViewComponent } from "../workbench/deploys-view.component";
import { PerformanceViewComponent } from "../workbench/performance-view.component";
import { TrendsViewComponent } from "../workbench/trends-view.component";
import { WorkbenchComponent } from "../workbench/workbench.component";

export function rootStateOnEnter(
  $transition$: Transition,
  _$state$: StateDeclaration,
) {
  const rootUrlHandlerService = $transition$
    .injector()
    .get(RootUrlHandlerService);
  return rootUrlHandlerService.goToDefaultState();
}

export const rootState: Ng2StateDeclaration = {
  name: "root-url-handler",
  url: "/",
  onEnter: rootStateOnEnter,
};

export function workbenchStateUserResolve(userService: UserService) {
  return userService.current().toPromise();
}

export function workbenchStateProjectsResolve(projectService: ProjectService) {
  return projectService
    .query()
    .toPromise()
    .then((data: any) => data.projects);
}

export function workbenchStateProjectResolve(): any {
  return null;
}

export const workbenchState: Ng2StateDeclaration = {
  name: "workbench",
  abstract: true,
  resolve: [
    {
      token: "projects",
      deps: [ProjectService],
      resolveFn: workbenchStateProjectsResolve,
    },
    {
      token: "project",
      resolveFn: workbenchStateProjectResolve,
    },
    {
      token: "user",
      deps: [UserService],
      resolveFn: workbenchStateUserResolve,
    },
  ],
  views: {
    workbench: {
      component: WorkbenchComponent,
    },

    "project-picker@workbench": {
      component: ProjectPickerComponent,
    },
  },
};

export function accountStateProjectResolve(): any {
  return null;
}

export const accountState: Ng2StateDeclaration = {
  name: "account",
  parent: "workbench",
  url: "?dur&last_n",
  params: {
    dur: {
      dynamic: true,
      type: "decodableString",
    },
    last_n: {
      dynamic: true,
      type: "decodableString",
    },
  },
  abstract: true,
  resolve: [{ token: "project", resolveFn: accountStateProjectResolve }],
  views: {
    "main@workbench": {
      component: AccountViewComponent,
    },
  },
};

export function projectStateProjectResolve(
  $transition$: Transition,
  projectService: ProjectService,
) {
  return projectService
    .get({ projectId: $transition$.params().projectId })
    .toPromise();
}

export const projectState: Ng2StateDeclaration = {
  name: "project",
  parent: "workbench",
  url: "/projects/{projectId}?dur&last_n",
  params: {
    dur: {
      dynamic: true,
      type: "decodableString",
    },
    last_n: {
      dynamic: true,
      type: "decodableString",
    },
  },
  abstract: true,
  resolve: [
    {
      token: "project",
      deps: [Transition, ProjectService],
      resolveFn: projectStateProjectResolve,
    },
  ],
  views: {
    "onboarding-checklist@workbench": {
      component: OnboardingChecklistComponent,
    },

    "onboarding-progress@workbench": {
      component: OnboardingProgressComponent,
    },

    "project-language-icon@workbench": {
      component: ProjectLanguageIconComponent,
    },

    "project-picker@workbench": {
      component: ProjectPickerComponent,
    },
    "project-nav@workbench": {
      component: ProjectNavComponent,
    },
  },
};

export function errorsStateEmptyProjectResolve(
  $transition$: Transition,
  Group: GroupService,
  project: any,
  airbrake: AirbrakeService,
) {
  // A project can have no errors if all were deleted (e.g. by a short
  // retention period), so we check `lastNoticeAt`. This field can take a
  // few minutes to update so we also check for the existence of errors.
  if (project && project.lastNoticeAt) {
    return false;
  }

  return Group.query({ projectId: $transition$.params().projectId, limit: 1 })
    .toPromise()
    .then((resp: any) => {
      if (!resp.groups) {
        return true;
      }
      return resp.groups.length === 0;
    })
    .catch((resp: HttpErrorResponse) => {
      airbrake.notifier.notify({
        error: "Couldn't fetch groups",
        params: resp,
      });
      return true;
    });
}

export const errorsState: Ng2StateDeclaration = {
  name: "errors",
  parent: "project",
  abstract: true,
  resolve: [
    {
      token: "emptyProject",
      deps: [Transition, GroupService, "project", AirbrakeService],
      resolveFn: errorsStateEmptyProjectResolve,
    },
  ],
  views: {
    "main@workbench": {
      component: ErrorsComponent,
    },
  },
};

export const deploysState: Ng2StateDeclaration = {
  name: "deploys",
  parent: "project",
  abstract: true,
  views: {
    "main@workbench": {
      component: DeploysViewComponent,
    },
  },
};

export const performanceState: Ng2StateDeclaration = {
  name: "performance",
  parent: "project",
  abstract: true,
  views: {
    "main@workbench": {
      component: PerformanceViewComponent,
    },
  },
};

export function projectPerformanceStateEmptyProjectResolve(project: any) {
  // lastRequestAt can take a few minutes to update, but that's an okay
  // compromise here, whereas checking for errors deserves quicker user
  // feedback. This compromise buys us this simpler code.
  return project && typeof project.lastRequestAt !== "string";
}

export const projectPerformanceState: Ng2StateDeclaration = {
  name: "project-performance",
  url: "/performance?method&environment&sortColumn&sortQueryColumn&sortQueueColumn&sortDesc&sortQueryDesc&sortQueueDesc&page&queryPage&routeSearch&querySearch&queueSearch&currentTab&queuePage",
  params: {
    method: {
      dynamic: true,
    },
    environment: {
      dynamic: true,
      replace: [{ from: "", to: "" }],
    },
    sortColumn: {
      dynamic: true,
    },
    sortQueryColumn: {
      dynamic: true,
      value: "stat.count",
      squash: true,
    },
    sortQueueColumn: {
      dynamic: true,
      value: "stat.count",
      squash: true,
    },
    sortDesc: {
      dynamic: true,
    },
    sortQueryDesc: {
      dynamic: true,
      type: "bool",
      value: true,
      squash: true,
    },
    sortQueueDesc: {
      dynamic: true,
      type: "bool",
      value: true,
      squash: true,
    },
    page: {
      dynamic: true,
    },
    queryPage: {
      dynamic: true,
      type: "int",
      value: 1,
      squash: true,
    },
    routeSearch: {
      dynamic: true,
      type: "decodableString",
    },
    querySearch: {
      dynamic: true,
      type: "decodableString",
    },
    queueSearch: {
      dynamic: true,
      type: "decodableString",
    },
    currentTab: {
      dynamic: true,
      value: "routes",
      squash: true,
    },
    queuePage: {
      dynamic: true,
      type: "int",
      value: 1,
      squash: true,
    },
  },
  parent: "performance",
  views: {
    "project-performance@performance": {
      component: ProjectPerformanceComponent,
    },
  },
  resolve: [
    {
      token: "emptyProject",
      deps: ["project"],
      resolveFn: projectPerformanceStateEmptyProjectResolve,
    },
  ],
};

export const routesShowState: Ng2StateDeclaration = {
  name: "routes-show",
  url: "/:routeId?status&responseType",
  parent: "project-performance",
  views: {
    "project-performance@performance": {
      component: RouteViewComponent,
    },
  },
  params: {
    status: {
      dynamic: true,
      squash: true,
      value: "total",
    },
    responseType: {
      dynamic: false,
      replace: [{ from: "", to: "" }],
    },
    page: {
      dynamic: true,
    },
    sortColumn: {
      dynamic: true,
    },
    sortDesc: {
      dynamic: true,
    },
  },
};

export const accountOverviewState: Ng2StateDeclaration = {
  name: "account-overview",
  url: "/projects",
  parent: "account",
  views: {
    "account-overview@account": {
      component: AccountOverviewComponent,
    },
  },
};

export function projectDeploysStateEmptyProjectResolve(project: any) {
  return project && typeof project.lastDeployAt !== "string";
}

export const projectDeploysState: Ng2StateDeclaration = {
  name: "project-deploys",
  url: "/deploys?page&environment&chartType",
  parent: "deploys",
  params: {
    chartType: {
      value: "group",
      dynamic: true,
    },
    environment: {
      value: "all",
      dynamic: true,
    },
    page: {
      dynamic: true,
      type: "int",
      value: 1,
    },
  },
  resolve: [
    {
      token: "emptyProject",
      deps: ["project"],
      resolveFn: projectDeploysStateEmptyProjectResolve,
    },
  ],
  views: {
    "project-deploys@deploys": {
      component: ProjectDeploysComponent,
    },
  },
};

export function deploysShowStateDeployResolve(
  $transition$: Transition,
  deployService: DeployService,
) {
  return deployService.get($transition$.params()).toPromise();
}

export function deploysShowStatePrevResolve(
  $transition$: Transition,
  deployService: DeployService,
) {
  // Catch 404s because a deploy does not require a previous deploy.
  return deployService
    .prev($transition$.params())
    .toPromise()
    .catch(() => {
      return;
    });
}

export function deploysShowStateNextResolve(
  $transition$: Transition,
  deployService: DeployService,
) {
  // Catch 404s because a deploy does not require a next deploy.
  return deployService
    .next($transition$.params())
    .toPromise()
    .catch((_error) => {
      return;
    });
}

export const deploysShowState: Ng2StateDeclaration = {
  name: "deploys-show",
  url: "/:deployId",
  parent: "project-deploys",
  resolve: [
    {
      token: "deploy",
      deps: [Transition, DeployService],
      resolveFn: deploysShowStateDeployResolve,
    },
    {
      token: "prev",
      deps: [Transition, DeployService],
      resolveFn: deploysShowStatePrevResolve,
    },
    {
      token: "next",
      deps: [Transition, DeployService],
      resolveFn: deploysShowStateNextResolve,
    },
  ],
  views: {
    "project-deploys@deploys": {
      component: DeployViewComponent,
    },
  },
};

export function projectErrorsStateFiltersResolve(
  $transition$: Transition,
  filterParamsService: FilterParamsService,
) {
  const params = $transition$.params();
  const filters = { projectId: params.projectId };

  return filterParamsService
    .fromState(params)
    .toPromise()
    .then((newFilters: any) => merge(filters, newFilters));
}

export const projectErrorsState: Ng2StateDeclaration = {
  name: "project-errors",
  url: "?filters",
  parent: "errors",
  params: {
    filters: {
      dynamic: true,
      squash: true,
      type: "decodableJSON",
      value: {
        order: "last_notice",
        resolved: "false",
      },
    },
  },
  resolve: [
    {
      token: "filters",
      deps: [Transition, FilterParamsService],
      resolveFn: projectErrorsStateFiltersResolve,
    },
  ],
  views: {
    "notice@errors": {
      component: ProjectDetailComponent,
    },

    "groups@errors": {
      component: GroupListComponent,
    },

    "filters@errors": {
      component: GroupFilterComponent,
    },
  },
};

export function groupsShowStateOnEnter($transition$: Transition) {
  const group = $transition$.injector().get("group");
  const noticeQuery = $transition$.injector().get("noticeQuery");
  if (group.redirectTo) {
    return group.redirectTo;
  }
  if (noticeQuery.redirectTo) {
    return noticeQuery.redirectTo;
  }
}

export function groupsShowStateNoticeResolve(): any {
  return null;
}

export function groupsShowStateGroupResolve(
  $transition$: Transition,
  Group: GroupService,
) {
  const params = {
    projectId: $transition$.params().projectId,
    groupId: $transition$.params().groupId,
  };
  return Group.get(params)
    .toPromise()
    .catch((resp: any) => {
      if (resp.status !== 404) {
        return;
      }
      return {
        redirectTo: $transition$.router.stateService.target(
          "project-errors",
          $transition$.params(),
        ),
      };
    });
}

export function groupsShowStateActiveFiltersResolve(filters: any) {
  let filtering = false;
  for (const key of Object.keys(filters)) {
    const value = filters[key];
    if (key === "order" || key === "projectId") {
      continue;
    }
    if (key === "resolved" && value === "false") {
      continue;
    }
    filtering = true;
    break;
  }
  return filtering;
}

export function groupsShowStateNoticeQueryResolve(
  $transition$: Transition,
  noticeService: NoticeService,
  activeFilters: boolean,
  filters: any,
) {
  const baseParams = {
    projectId: $transition$.params().projectId,
    groupId: $transition$.params().groupId,
    limit: 1,
  };

  function redirect() {
    if (activeFilters) {
      // There are no notices for this group that match the search
      // criteria. Redirect to the same group without the search
      // params.
      return {
        redirectTo: $transition$.router.stateService.target(
          "groups-show",
          baseParams,
          { inherit: false, reload: true },
        ),
      };
    } else {
      // There are no notices at all for this group. Redirect to
      // project.
      return {
        redirectTo: $transition$.router.stateService.target(
          "project-errors",
          $transition$.params(),
        ),
      };
    }
  }
  return noticeService
    .query(merge({}, baseParams, filters))
    .toPromise()
    .then((resp: any) => {
      if (resp.count > 0 && resp.notices && resp.notices.length > 0) {
        return resp;
      } else {
        return redirect();
      }
    })
    .catch((_resp: any) => {
      return redirect();
    });
}

export const groupsShowState: Ng2StateDeclaration = {
  name: "groups-show",
  url: "/groups/:groupId?tab",
  parent: "project-errors",
  params: {
    tab: {
      dynamic: true,
      value: "overview",
    },
  },
  onEnter: groupsShowStateOnEnter,
  resolve: [
    {
      token: "notice",
      resolveFn: groupsShowStateNoticeResolve,
    },
    {
      token: "group",
      deps: [Transition, GroupService],
      resolveFn: groupsShowStateGroupResolve,
    },
    {
      token: "activeFilters",
      deps: ["filters"],
      resolveFn: groupsShowStateActiveFiltersResolve,
    },
    {
      token: "noticeQuery",
      deps: [Transition, NoticeService, "activeFilters", "filters"],
      resolveFn: groupsShowStateNoticeQueryResolve,
    },
  ],
  views: {
    "notice@errors": {
      component: NoticeViewComponent,
    },
    "overview@groups-show": {
      component: NoticeOverviewComponent,
    },
  },
};

export function noticesShowStateNoticeResolve(
  $transition$: Transition,
  noticeService: NoticeService,
) {
  return noticeService
    .get({
      projectId: $transition$.params().projectId,
      groupId: $transition$.params().groupId,
      noticeId: $transition$.params().noticeId,
    })
    .toPromise()
    .then((resp: any) => resp.notice);
}

export const noticesShowState: Ng2StateDeclaration = {
  name: "notices-show",
  url: "/notices/:noticeId",
  parent: "groups-show",
  resolve: [
    {
      token: "notice",
      deps: [Transition, NoticeService],
      resolveFn: noticesShowStateNoticeResolve,
    },
  ],
  views: {
    "notice@errors": {
      component: NoticeViewComponent,
    },
    "overview@notices-show": {
      component: NoticeOverviewComponent,
    },
  },
};

export const alertsState: Ng2StateDeclaration = {
  name: "alerts",
  parent: "project",
  abstract: true,
  views: {
    "main@workbench": {
      component: AlertsViewComponent,
    },
  },
};

export function projectAlertsStateEmptyProjectResolve(project: any) {
  // lastRequestAt can take a few minutes to update, but that's an okay
  // compromise here, whereas checking for errors deserves quicker user
  // feedback. This compromise buys us this simpler code.
  return project && typeof project.lastRequestAt !== "string";
}

export const projectAlertsState: Ng2StateDeclaration = {
  name: "project-alerts",
  url: "/alerts?page",
  params: {
    page: {
      value: "1",
      dynamic: true,
    },
  },
  parent: "alerts",
  views: {
    "project-alerts@alerts": {
      component: ProjectAlertsComponent,
    },
  },
  resolve: [
    {
      token: "emptyProject",
      deps: ["project"],
      resolveFn: projectAlertsStateEmptyProjectResolve,
    },
  ],
};

export function projectTrendsStateMissingErrorDataResolve(project: any) {
  return project && typeof project.lastNoticeAt !== "string";
}

export function projectTrendsStateMissingDeployDataResolve(project: any) {
  return project && typeof project.lastDeployAt !== "string";
}

export function projectTrendsStateMissingPerformanceDataResolve(project: any) {
  return project && typeof project.lastRequestAt !== "string";
}

export const trendsState: Ng2StateDeclaration = {
  name: "trends",
  parent: "project",
  abstract: true,
  views: {
    "main@workbench": {
      component: TrendsViewComponent,
    },
  },
};

export const projectTrendsState: Ng2StateDeclaration = {
  name: "project-trends",
  url: "/trends?digestId",
  params: {
    digestId: {
      type: "int",
    },
  },
  parent: "trends",
  resolve: [
    {
      token: "missingErrorData",
      deps: ["project"],
      resolveFn: projectTrendsStateMissingErrorDataResolve,
    },
    {
      token: "missingDeployData",
      deps: ["project"],
      resolveFn: projectTrendsStateMissingDeployDataResolve,
    },
    {
      token: "missingPerformanceData",
      deps: ["project"],
      resolveFn: projectTrendsStateMissingPerformanceDataResolve,
    },
  ],
  views: {
    "project-trends@trends": {
      component: ProjectTrendsComponent,
    },
  },
};

export const APP_STATES = [
  rootState,
  workbenchState,
  accountState,
  projectState,
  errorsState,
  deploysState,
  performanceState,
  projectPerformanceState,
  routesShowState,
  accountOverviewState,
  projectDeploysState,
  deploysShowState,
  projectErrorsState,
  groupsShowState,
  noticesShowState,
  alertsState,
  projectAlertsState,
  trendsState,
  projectTrendsState,
];

// workbench (*)
//   account (*)
//     account-overview
//   project (*)
//     deploys (*)
//       project-deploys
//         deploys-show
//     performance (*)
//       project-performance
//         routes-show
//     errors (*)
//       project-errors
//         groups-show
//           notices-show
//     alerts (*)
//       project-alerts
